import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNotice } from '../../components/notice.js';
import { Badge, Button, Callout, Card } from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { readFileAsBase64 } from '../../lib/file.js';
import { shrinkPhoto } from '../../lib/photo-shrink.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import {
  PHOTO_ACCEPT,
  photoColorChoices,
  photoKindRejection,
  photoRejection,
} from './photo-files.js';

/**
 * A style's photos (SCOPE §5.4, decisions 36 and 141).
 *
 * One card on the style, where the buyer already is. A photo belongs to one
 * colourway or to the whole style; the first one is the one shown when there
 * is no better match. It is usable the moment it is added — the original
 * stands in while the queue makes its sizes, and the card says so.
 */
export function StylePhotos({
  productId,
  styleName,
  variants,
  canManage,
}: {
  productId: string;
  /** For a notice saying whose photos changed. */
  styleName: string;
  variants: readonly { colorName: string | null }[];
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const notify = useNotice();
  const picker = useRef<HTMLInputElement>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; of: number } | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const available = useQuery({
    queryKey: ['images', 'available'],
    queryFn: () => api.images.available.query(),
    staleTime: 5 * 60_000,
  });
  const photos = useQuery({
    queryKey: ['images', productId],
    queryFn: () => api.images.list.query({ productId }),
    // While sizes are being made, look again until they are.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((photo) => !photo.processed) ? 2_000 : false,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['images', productId] });

  const add = useMutation({
    mutationFn: async (files: File[]) => {
      const refused: string[] = [];
      const wanted = files.filter((file) => {
        const reason = photoKindRejection(file);
        if (reason !== null) refused.push(reason);
        return reason === null;
      });
      setProgress({ done: 0, of: wanted.length });
      let added = 0;
      // One at a time: each is up to 15 MB, and the order they were picked in
      // is the order they land in.
      for (const [index, file] of wanted.entries()) {
        try {
          // Shrunk first — see photo-shrink.ts for why a photo never goes up
          // whole — and only then held to the size limit.
          const sending = await shrinkPhoto(file);
          const tooLarge = photoRejection(sending);
          if (tooLarge !== null) throw new Error(tooLarge);
          await api.images.upload.mutate({
            productId,
            contentBase64: await readFileAsBase64(sending),
          });
          added += 1;
        } catch (error) {
          refused.push(`${file.name}: ${errorMessage(error)}`);
        }
        setProgress({ done: index + 1, of: wanted.length });
      }
      return { added, refused };
    },
    onSuccess: ({ added, refused }) => {
      if (added > 0) {
        recordBreadcrumb('products', `added ${added} photo(s)`);
        notify({
          title: `Added — ${added} ${added === 1 ? 'photo' : 'photos'} to ${styleName}`,
          ...(refused.length > 0
            ? { detail: `${refused.length} could not be added — see below.` }
            : {}),
        });
      }
      setProblems(refused);
      setProgress(null);
      void refresh();
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; colorName?: string | null; altText?: string | null }) =>
      api.images.update.mutate(input),
    onSuccess: () => void refresh(),
  });
  const move = useMutation({
    mutationFn: (input: { id: string; direction: 'earlier' | 'later' }) =>
      api.images.move.mutate(input),
    onSuccess: (list) => queryClient.setQueryData(['images', productId], list),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.images.remove.mutate({ id }),
    onSuccess: () => {
      notify({ title: `Removed — a photo of ${styleName}` });
      setConfirmRemove(null);
      void refresh();
    },
  });

  const colors = photoColorChoices(variants);
  const list = photos.data ?? [];
  const canAdd = canManage && available.data?.available === true;

  return (
    <Card
      title={`Photos${list.length > 0 ? ` · ${list.length}` : ''}`}
      actions={
        canManage ? (
          <>
            <input
              ref={picker}
              type="file"
              accept={PHOTO_ACCEPT}
              multiple
              hidden
              aria-label="Choose photos"
              onChange={(event) => {
                const files = Array.from(event.target.files ?? []);
                event.target.value = '';
                if (files.length > 0) add.mutate(files);
              }}
            />
            <Button
              variant="ghost"
              busy={add.isPending}
              disabledReason={
                available.data?.available === false
                  ? 'Photo storage is not set up for this store yet'
                  : undefined
              }
              onClick={() => picker.current?.click()}
            >
              {progress === null
                ? 'Add photos'
                : `Adding ${Math.min(progress.done + 1, progress.of)} of ${progress.of}…`}
            </Button>
          </>
        ) : undefined
      }
    >
      {available.data?.available === false ? (
        <p className="text-secondary">
          Photo storage is not set up for this store yet. Everything else works as usual.
        </p>
      ) : null}

      {problems.length > 0 ? (
        <Callout tone="danger" title="Not added">
          <ul className="photos__problems">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </Callout>
      ) : null}

      {list.length === 0 && available.data?.available !== false ? (
        <div className="photos__empty">
          <div className="photos__placeholder" aria-hidden="true" />
          <p className="text-secondary">
            No photos yet.
            {canAdd ? ' Add one — the register and the storefront show it.' : ''}
          </p>
        </div>
      ) : null}

      {list.length > 0 ? (
        <ul className="photos">
          {list.map((photo, index) => (
            <li key={photo.id} className="photo">
              <img
                className="photo__image"
                src={photo.urls.register}
                // The larger size when the tile is larger than 320px (a tablet).
                srcSet={`${photo.urls.register} 320w, ${photo.urls.web} 800w`}
                sizes="(max-width: 900px) 50vw, 280px"
                alt={photo.altText ?? ''}
                width={160}
                height={
                  photo.width !== null && photo.height !== null
                    ? Math.round((160 * photo.height) / photo.width)
                    : 160
                }
              />
              <div className="photo__facts">
                {index === 0 ? <Badge tone="info">Shown first</Badge> : null}
                {!photo.processed ? <Badge tone="neutral">Preparing sizes…</Badge> : null}
              </div>

              {canManage ? (
                <div className="photo__controls">
                  <label className="field">
                    <span className="field__label">Shows</span>
                    <select
                      className="select"
                      value={photo.colorName ?? ''}
                      onChange={(event) =>
                        update.mutate({ id: photo.id, colorName: event.target.value || null })
                      }
                    >
                      <option value="">The whole model</option>
                      {colors.map((color) => (
                        <option key={color} value={color}>
                          {color}
                        </option>
                      ))}
                      {photo.colorName !== null &&
                      !colors.some(
                        (color) => color.toLowerCase() === photo.colorName!.toLowerCase(),
                      ) ? (
                        <option value={photo.colorName}>{photo.colorName}</option>
                      ) : null}
                    </select>
                  </label>
                  <label className="field">
                    <span className="field__label">Description</span>
                    <input
                      className="input"
                      defaultValue={photo.altText ?? ''}
                      placeholder="e.g. Front view, hood up"
                      onBlur={(event) => {
                        const next = event.target.value.trim();
                        if (next !== (photo.altText ?? '')) {
                          update.mutate({ id: photo.id, altText: next || null });
                        }
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') event.currentTarget.blur();
                      }}
                    />
                  </label>
                  <div className="photo__buttons">
                    <Button
                      variant="ghost"
                      aria-label="Show earlier"
                      disabledReason={index === 0 ? 'Already first' : undefined}
                      onClick={() => move.mutate({ id: photo.id, direction: 'earlier' })}
                    >
                      ←
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label="Show later"
                      disabledReason={index === list.length - 1 ? 'Already last' : undefined}
                      onClick={() => move.mutate({ id: photo.id, direction: 'later' })}
                    >
                      →
                    </Button>
                    <Button
                      variant="danger"
                      busy={remove.isPending && remove.variables === photo.id}
                      onClick={() =>
                        confirmRemove === photo.id
                          ? remove.mutate(photo.id)
                          : setConfirmRemove(photo.id)
                      }
                    >
                      {confirmRemove === photo.id ? 'Really remove' : 'Remove'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-secondary photo__caption">
                  {photo.colorName ?? 'The whole model'}
                </p>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      {update.isError || move.isError || remove.isError ? (
        <Callout tone="danger">{errorMessage(update.error ?? move.error ?? remove.error)}</Callout>
      ) : null}
    </Card>
  );
}
